import { useMemo } from 'react';

import { ListItem, Autocomplete, ListItemText } from '@cast/design-system';
import { Workload } from '@cast/types';

import { WorkloadBaseRunningInNode } from '../../types';
import { PreparedNode } from '../types';

import { useSearchContext, useSearchCriterion } from '@cast/shared-search-ui';

export type WorkloadsSelectOption = Pick<
  WorkloadBaseRunningInNode,
  'name' | 'namespace'
>;
type Props = {
  workloads?: Workload[];
};

const optionKey = (option: WorkloadsSelectOption) =>
  `${option.namespace}::${option.name}`;

export const WorkloadsSelect = ({ workloads }: Props) => {
  const { value, setValue, clear } = useSearchCriterion('workloads');
  const search = useSearchContext<PreparedNode>();

  const options = useMemo<WorkloadsSelectOption[]>(() => {
    const map = new Map<string, WorkloadsSelectOption>();

    const add = (name?: string, namespace?: string) => {
      if (!name) {
        return;
      }
      const option: WorkloadsSelectOption = {
        name,
        namespace: namespace || '',
      };
      map.set(optionKey(option), option);
    };

    for (const workload of workloads ?? []) {
      add(workload.name, workload.namespace);
    }

    // Fallback when the cluster workloads query is empty/disabled but nodes
    // already have attached workloads (same source the filter criterion uses).
    for (const node of search?.data ?? []) {
      for (const workload of node.workloads ?? []) {
        add(workload.name, workload.namespace);
      }
    }

    return [...map.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [workloads, search?.data]);

  return (
    <Autocomplete
      testId="node-list-workloads-filter"
      size="md"
      inputText="Workload"
      checkable
      showSelectAll={false}
      multiple
      value={value}
      onChange={(option) => {
        if (option?.length) {
          setValue?.(option);
        } else {
          clear?.();
        }
      }}
      searchFilter={(option, searchValue) => {
        return option.name
          .toLocaleLowerCase()
          .includes(searchValue.toLocaleLowerCase());
      }}
      optionLabel={(option) => option.name}
      optionKey={optionKey}
      isOptionEqualToValue={(option, selected) =>
        option.name === selected.name && option.namespace === selected.namespace
      }
      options={options}
      virtualization
      virtualizationProps={{
        maxHeight: 250,
        minWidth: 310,
      }}
      listSx={{
        width: 310,
      }}
      sx={{ width: '100%' }}
      renderOption={(option, itemProps) => {
        return (
          <ListItem {...itemProps}>
            <ListItemText
              sx={{
                maxWidth: 240,
                my: 10,
              }}
              primaryTypographyProps={{
                noWrap: true,
              }}
              primary={option.name}
              secondary={option.namespace}
            />
          </ListItem>
        );
      }}
    />
  );
};
